/**
 * A paragraph with inline formatting — bold, italic, underline and a size from
 * a fixed set — edited on a `contentEditable` surface that is **never the
 * data**.
 *
 * The owner's words were "select some text and mark them bold, italics,
 * underline, increase font size". The contract answers with runs on the text
 * block (`TextBlock.runs`), and this component is the smallest thing that edits
 * them: a surface, a toolbar of four controls, and three rules.
 *
 * **1. Every change is made to the runs, and the surface is redrawn from them.**
 * A mark is applied by `toggleFlag` / `setSize` in `lib/text-runs.ts`, over
 * offsets into the plain text, and the surface is then rebuilt by
 * `renderRuns` — `createElement` and `createTextNode`, no HTML string anywhere.
 * `document.execCommand` is not called: it writes whichever markup the browser
 * prefers, and the stored document would inherit the browser's opinions.
 * Ctrl/⌘+B, I and U are taken before the browser sees them and routed to the
 * same functions as the buttons, and any other `format…` input is cancelled.
 *
 * **2. Typing is read back through a whitelist, and nothing else is.** Ordinary
 * typing changes characters in text nodes and is left alone — redrawing on every
 * keystroke would move the caret and break an IME composition, which for a
 * Hindi or Marathi keyboard is every word. After each input the surface is read
 * by `readRuns`, which recognises only the four mark elements `renderRuns`
 * writes and reduces everything else to its text, and it is redrawn **only**
 * when what it holds is not exactly what the runs would draw. So whatever a
 * browser, an extension or an undo leaves behind is shown as what it will be
 * saved as within one input event.
 *
 * **3. Paste and drop never write HTML.** A paste takes `text/plain` from the
 * clipboard and inserts it through `insertText`; the `text/html` flavour — a
 * Word fragment's `<style>`, its `class="MsoNormal"`, an `<img>` a web page
 * copied along with a sentence — is never read. Drops are refused outright:
 * dragging text inside the paragraph would otherwise be a browser-authored
 * insertion, and dropping from outside is a paste without the clipboard's
 * plain-text flavour to fall back on.
 *
 * ## A mark on a variable covers the whole variable
 *
 * `{{ name }}` split by a mark compiles to HTML the per-recipient substitution
 * can never match. The contract refuses that block at save. The editor widens
 * the selection to the whole token before marking (`wholeVariableRange`) and
 * shows the widened selection, so the person sees what was marked; after
 * typing, `keepVariablesWhole` repairs a token assembled across two runs. See
 * `lib/text-runs.ts` for why widening won over refusing.
 *
 * ## The toolbar is always there, and live only over a selection
 *
 * It was going to appear on focus only, and that breaks the size picker: it is
 * the shared `Select`, which opens in a portal, so choosing a size moves focus
 * out of this block and a focus-driven toolbar would vanish under the pointer.
 * So the controls are always drawn, disabled until text is selected here, and
 * they act on the last selection made in this paragraph — kept across the
 * picker's focus move, and dropped when another field takes the typing focus.
 * Buttons take the click on `mousedown` without moving focus, so the selection
 * stays visible after Bold.
 *
 * ## What was rejected
 *
 * A rich-text library — `lib/text-runs.ts` says why. And a `<textarea>` with a
 * separate "formatting" list beside it, which keeps the DOM out of the question
 * entirely and asks a person to format text they cannot see formatted.
 *
 * ## The one thing this surface does not do
 *
 * The browser's own undo is left in place. After a redraw it may restore
 * markup it remembers; whatever it restores is read back through the walker
 * like any other input, so the worst it can produce is an undo that does less
 * than expected — never a stored document holding HTML.
 */
import { Button, Select, cn, useFieldProps } from '@wend/ui';
import { Bold, Italic, Underline } from 'lucide-react';
import {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent as ReactClipboardEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';

import type { TextBlock } from '@wend/shared';

import {
  BODY_TEXT_SIZE,
  TEXT_RUN_SIZES,
  TEXT_RUN_SIZE_LABEL,
  insertText,
  isTextRunSize,
  joinRuns,
  keepVariablesWhole,
  runsOfBlock,
  sameRuns,
  selectionMarks,
  setSize,
  toggleFlag,
  wholeVariableRange,
  withRuns,
  type TextRun,
  type TextRunFlag,
  type TextRunSize,
} from '../../lib/text-runs';
import { isCanonical, readRuns, renderRuns, selectRange, selectionOffsets } from '../../lib/text-runs-dom';

/** What the block card can ask of the paragraph: a variable at the caret. */
export interface ParagraphEditorHandle {
  insert: (token: string) => void;
}

interface Range {
  readonly start: number;
  readonly end: number;
}

const SIZE_OPTIONS = TEXT_RUN_SIZES.map((size) => ({
  value: String(size),
  label: TEXT_RUN_SIZE_LABEL[size],
}));

const FLAG_CONTROLS: readonly { flag: TextRunFlag; label: string; icon: ReactNode }[] = [
  { flag: 'bold', label: 'Bold', icon: <Bold aria-hidden="true" className="size-4" /> },
  { flag: 'italic', label: 'Italic', icon: <Italic aria-hidden="true" className="size-4" /> },
  { flag: 'underline', label: 'Underline', icon: <Underline aria-hidden="true" className="size-4" /> },
];

/** Ctrl/⌘ + this key toggles that flag. */
const SHORTCUT: Readonly<Record<string, TextRunFlag>> = { b: 'bold', i: 'italic', u: 'underline' };

/** The `beforeinput` types that are a formatting command, and what each maps to. */
const FORMAT_INPUT: Readonly<Record<string, TextRunFlag>> = {
  formatBold: 'bold',
  formatItalic: 'italic',
  formatUnderline: 'underline',
};

export function ParagraphEditor({
  block,
  label,
  disabled,
  onChange,
  ref,
}: {
  block: TextBlock;
  /** The accessible name — a `<label for>` does not name a `contentEditable`. */
  label: string;
  disabled: boolean;
  onChange: (block: TextBlock) => void;
  ref?: Ref<ParagraphEditorHandle>;
}) {
  const fieldProps = useFieldProps();
  const surface = useRef<HTMLDivElement>(null);

  /** The runs the surface is drawn from. Null until the first draw. */
  const drawn = useRef<TextRun[] | null>(null);
  const composing = useRef(false);

  /** The last selection made in this paragraph — see "The toolbar" above. */
  const [selection, setSelection] = useState<Range | null>(null);
  const kept = useRef<Range | null>(null);
  const keep = (next: Range | null): void => {
    const previous = kept.current;
    if (previous?.start === next?.start && previous?.end === next?.end) return;
    kept.current = next;
    setSelection(next);
  };

  /** The props as of the last render, for listeners attached once. */
  const latest = useRef({ block, onChange, disabled });
  useLayoutEffect(() => {
    latest.current = { block, onChange, disabled };
  });

  /* ---- drawing from the block ------------------------------------------- */

  /*
   * The block arrived — first render, a save, a reload, or this component's own
   * `onChange` coming back round. Redrawn only when the runs actually differ
   * from the ones on the surface, so the round trip of an ordinary keystroke
   * touches nothing.
   */
  useLayoutEffect(() => {
    const root = surface.current;
    if (root === null) return;
    const incoming = runsOfBlock(block);
    if (drawn.current !== null && sameRuns(drawn.current, incoming)) return;

    const focused = root.ownerDocument.activeElement === root;
    const caret = focused ? selectionOffsets(root) : null;
    drawn.current = incoming;
    renderRuns(root, incoming);
    if (caret !== null) {
      const length = joinRuns(incoming).length;
      selectRange(root, Math.min(caret.start, length), Math.min(caret.end, length));
    }
  }, [block]);

  /* ---- committing an edit ------------------------------------------------ */

  /**
   * New runs for this paragraph: normalised with no variable cut, drawn if the
   * surface does not already show them, and handed up if they differ from the
   * block's. `select` is where the selection belongs afterwards; without it the
   * caret is kept where it is.
   */
  const commit = (next: readonly TextRun[], select?: Range): void => {
    const root = surface.current;
    if (root === null) return;
    const runs = keepVariablesWhole(next);
    const focused = root.ownerDocument.activeElement === root;

    // The selection is put back only when the surface was redrawn or an edit
    // says where it belongs. Re-setting it after every keystroke would be
    // invisible on a desktop and would reset the composing region a phone
    // keyboard keeps for autocorrect.
    const redraw = !isCanonical(root, runs);
    const caret = select ?? (focused && redraw ? selectionOffsets(root) : null);
    drawn.current = runs;
    if (redraw) renderRuns(root, runs);
    if (focused && caret !== null) {
      const length = joinRuns(runs).length;
      selectRange(root, Math.min(caret.start, length), Math.min(caret.end, length));
    }
    if (select !== undefined) keep(select);

    const current = latest.current.block;
    if (!sameRuns(runs, runsOfBlock(current))) latest.current.onChange(withRuns(current, runs));
  };

  /** Where an edit applies: the live selection here, else the kept one, else the end. */
  const target = (): Range => {
    const root = surface.current;
    const live = root === null ? null : selectionOffsets(root);
    if (live !== null) return live;
    if (kept.current !== null) return kept.current;
    const length = joinRuns(drawn.current ?? []).length;
    return { start: length, end: length };
  };

  const replaceSelection = (text: string): void => {
    if (latest.current.disabled) return;
    const runs = drawn.current ?? [];
    const { start, end } = target();
    const caret = start + text.length;
    commit(insertText(runs, start, end, text), { start: caret, end: caret });
  };

  /** A mark over the selection, widened to whole variables first. */
  const mark = (apply: (runs: readonly TextRun[], range: Range) => TextRun[]): void => {
    if (latest.current.disabled) return;
    const runs = drawn.current ?? [];
    const { start, end } = target();
    if (start === end) return;
    const range = wholeVariableRange(joinRuns(runs), start, end);
    commit(apply(runs, range), range);
  };

  const toggle = (flag: TextRunFlag): void => {
    mark((runs, range) => toggleFlag(runs, range.start, range.end, flag));
  };

  const resize = (size: TextRunSize): void => {
    mark((runs, range) => setSize(runs, range.start, range.end, size));
  };

  /** Read what typing did, through the walker. */
  const readBack = (): void => {
    const root = surface.current;
    if (root === null || composing.current) return;
    commit(readRuns(root).runs);
  };

  /** Handlers as of this render, for the listeners attached once below. */
  const actions = useRef({ toggle, replaceSelection, readBack, keep });
  actions.current = { toggle, replaceSelection, readBack, keep };

  useImperativeHandle(ref, () => ({
    insert: (token: string) => {
      actions.current.replaceSelection(token);
    },
  }));

  /* ---- listeners React does not model ------------------------------------ */

  useEffect(() => {
    const root = surface.current;
    if (root === null) return;
    const doc = root.ownerDocument;

    /*
     * `beforeinput`, natively: React's `onBeforeInput` is a polyfill of the old
     * `textInput` event and carries no `inputType`, which is the whole point.
     * Line breaks come here from keyboards that send no usable `keydown` —
     * Android's among them — so Enter is handled in both places.
     */
    const onBeforeInput = (event: InputEvent): void => {
      const type = event.inputType;
      if (type.startsWith('format')) {
        event.preventDefault();
        const flag = FORMAT_INPUT[type];
        if (flag !== undefined) actions.current.toggle(flag);
        return;
      }
      if (type === 'insertParagraph' || type === 'insertLineBreak') {
        event.preventDefault();
        actions.current.replaceSelection('\n');
        return;
      }
      if (type === 'insertFromDrop' || type === 'insertFromPaste' || type === 'insertFromPasteAsQuotation') {
        // Paste is taken on `paste`, as plain text; drops are refused.
        event.preventDefault();
      }
    };

    const onSelectionChange = (): void => {
      if (composing.current) return;
      const offsets = selectionOffsets(root);
      if (offsets !== null) {
        actions.current.keep(offsets);
        return;
      }
      // Another field took the typing focus: this paragraph's selection is no
      // longer the one a person means. A button or the size picker is not that.
      const active = doc.activeElement;
      if (
        active !== root &&
        active instanceof HTMLElement &&
        (active.isContentEditable || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')
      ) {
        actions.current.keep(null);
      }
    };

    root.addEventListener('beforeinput', onBeforeInput);
    doc.addEventListener('selectionchange', onSelectionChange);
    return () => {
      root.removeEventListener('beforeinput', onBeforeInput);
      doc.removeEventListener('selectionchange', onSelectionChange);
    };
  }, []);

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.nativeEvent.isComposing) return;
    const modifier = event.ctrlKey || event.metaKey;
    if (modifier && !event.altKey && !event.shiftKey) {
      const flag = SHORTCUT[event.key.toLowerCase()];
      if (flag !== undefined) {
        event.preventDefault();
        toggle(flag);
      }
      return;
    }
    if (event.key === 'Enter' && !modifier && !event.altKey) {
      event.preventDefault();
      replaceSelection('\n');
    }
  }

  function onPaste(event: ReactClipboardEvent<HTMLDivElement>): void {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n');
    if (text !== '') replaceSelection(text);
  }

  /* ---- the toolbar's state ----------------------------------------------- */

  const runs = runsOfBlock(block);
  const length = joinRuns(runs).length;
  const range =
    selection !== null && selection.start < selection.end && selection.end <= length ? selection : null;
  const marks = range === null ? null : selectionMarks(runs, range.start, range.end);
  const sizeValue =
    marks === null || marks.size === 'mixed' ? '' : String(marks.size ?? BODY_TEXT_SIZE);
  const idle = disabled || range === null;

  return (
    <div className="flex flex-col gap-2">
      <div role="toolbar" aria-label="Formatting" className="flex flex-wrap items-center gap-1">
        {FLAG_CONTROLS.map(({ flag, label: name, icon }) => (
          <Button
            key={flag}
            type="button"
            variant={marks?.[flag] === true ? 'secondary' : 'ghost'}
            size="sm"
            className="px-2.5"
            aria-label={name}
            aria-pressed={marks?.[flag] === true}
            disabled={idle}
            // Taken on mousedown so focus — and the selection — stay in the text.
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              toggle(flag);
            }}
          >
            {icon}
          </Button>
        ))}

        <div className="w-[9rem]">
          <Select
            aria-label="Text size"
            placeholder="Size"
            size="sm"
            value={sizeValue}
            options={SIZE_OPTIONS}
            disabled={idle}
            onValueChange={(next) => {
              const size = Number(next);
              if (isTextRunSize(size)) resize(size);
            }}
          />
        </div>
      </div>

      {/*
        The surface. No React children, ever: its content is drawn by
        `renderRuns` and read by `readRuns`, and React reconciling children
        here would fight both over the same nodes.
      */}
      <div
        ref={surface}
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        aria-disabled={disabled || undefined}
        contentEditable={!disabled}
        spellCheck
        {...fieldProps}
        onInput={(event) => {
          if ((event.nativeEvent as InputEvent).isComposing) return;
          readBack();
        }}
        onCompositionStart={() => {
          composing.current = true;
        }}
        onCompositionEnd={() => {
          composing.current = false;
          readBack();
        }}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onDrop={(event) => {
          event.preventDefault();
        }}
        className={cn(
          'block min-h-[7rem] w-full whitespace-pre-wrap break-words rounded-base border border-line-strong bg-ink/55 px-3.5 py-2.5',
          'text-[0.95rem] leading-normal text-text',
          'transition-[background-color,border-color,box-shadow] duration-150 ease-wend',
          'hover:border-white/25',
          'focus-visible:border-brand-a focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
          'aria-disabled:cursor-not-allowed aria-disabled:opacity-55',
          'aria-[invalid=true]:border-bad aria-[invalid=true]:focus-visible:outline-bad',
        )}
      />
    </div>
  );
}
